import { ApiTypes, api } from "../common";
import { handleApiError } from "~src/utils/errorHandler";
import { isAuthenticated } from "~src/utils/authService";

const AUTH_PATHS: ApiTypes.API = {
  login: {
    path: "api/user/login/",
    method: ApiTypes.HTTPMethod.POST,
  },
  register: {
    path: "api/user/register/",
    method: ApiTypes.HTTPMethod.POST,
  },
  me: {
    path: "api/user/me/",
    method: ApiTypes.HTTPMethod.GET,
  },
  verifyEmail: {
    path: "api/user/reset-password/",
    method: ApiTypes.HTTPMethod.POST,
  },
  verifyResetLink: {
    path: "api/user/verify-reset-link/",
    method: ApiTypes.HTTPMethod.GET,
  },
  resetPassword: {
    path: "api/user/confirm-reset-password/",
    method: ApiTypes.HTTPMethod.POST,
  },
};

type LoginResponse = {
  id: number;
  name: string;
  username: string;
  email: string;
  token: string;
  is_superuser?: boolean;
  role: string;
  is_beta_user?: boolean;
};

export const login = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const endpoint = AUTH_PATHS.login;
  const data = { email, password };
  
  try {
    const response = await api<LoginResponse>({
      method: endpoint.method,
      url: endpoint.path,
      data,
    });
    return response.data;
  } catch (error) {
    const apiError = handleApiError(error);
    
    // Special handling for authentication errors
    if (apiError.status === 400 || apiError.status === 401) {
      throw new Error("Incorrect Email or Password");
    }
    
    throw apiError;
  }
};

type RegisterResponse = {
  id: number;
  username: string;
  token: string;
  is_superuser: boolean;
  is_beta_user: boolean;
  name: string;
  role: string;
};

export const register = async (
  name: string,
  email: string,
  password: string,
  recaptchaToken: string,
) => {
  const endpoint = AUTH_PATHS.register;
  const data = { name, email, password, recaptchaToken };

  try {
    const response = await api<RegisterResponse>({
      method: endpoint.method,
      url: endpoint.path,
      data,
    });
    return response.data;
  } catch (error) {
    console.error('Registration error details:', error);
    const apiError = handleApiError(error);
    
    // Special handling for common registration errors
    if (apiError.status === 400) {
      const details = apiError.details as Record<string, string[]>;
      if (details?.email) {
        throw new Error(details.email[0] || 'Invalid email format');
      }
      if (details?.password) {
        throw new Error(details.password[0] || 'Invalid password format');
      }
      if (details?.name) {
        throw new Error(details.name[0] || 'Invalid name format');
      }
      if (details?.recaptchaToken) {
        throw new Error('reCAPTCHA verification failed. Please refresh the page and try again.');
      }
    }
    
    throw apiError;
  }
};

export const me = async () => {
  // Don't make the API call if user is not authenticated
  if (!isAuthenticated()) {
    throw new Error("User not authenticated");
  }
  
  const endpoint = AUTH_PATHS.me;

  try {
    const response = await api<LoginResponse>({
      method: endpoint.method,
      url: endpoint.path,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const verifyEmail = async (email: string, recaptchaToken: string) => {
  const endpoint = AUTH_PATHS.verifyEmail;
  const data = { email, recaptchaToken };

  try {
    const response = await api({
      method: endpoint.method,
      url: endpoint.path,
      data,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const verifyResetLink = async (userId: string, sessionToken: string) => {
  const endpoint = AUTH_PATHS.verifyResetLink;

  try {
    const response = await api<{ email: string; message: string }>({
      method: endpoint.method,
      url: `${endpoint.path}${userId}/${sessionToken}/`,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const resetPassword = async (
  data: {
    password: string;
    confirm_password: string;
    recaptchaToken: string;
  },
  userId: string,
  sessionToken: string,
) => {
  const endpoint = AUTH_PATHS.resetPassword;
  
  try {
    const response = await api({
      method: endpoint.method,
      url: `${endpoint.path}${userId}/${sessionToken}/`,
      data,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
